/**
 * Loading a screen, or a heavy widget, on demand.
 *
 * WHY. Until 2026-10-13 `routes/tree.tsx` imported every screen statically and
 * the production build was one 2 MB file — Blockly included — on every route,
 * the landing page and the documentation among them. Everything below exists
 * so that a route downloads the code it draws and nothing else.
 *
 * THREE PIECES, AND WHY NOT `React.lazy` ALONE:
 *
 *  - `lazyScreen(importer, name)` is TanStack's `lazyRouteComponent`. Unlike
 *    `React.lazy` it has a `preload()`, and it survives a deploy: when the
 *    chunk an open tab asks for is gone (the image was replaced and the hash
 *    changed) it reloads the page ONCE instead of leaving the user on an error
 *    screen. That case is not hypothetical — every deploy creates it.
 *
 *  - `screen(Component, ...chunks)` hangs a `preload` on a route component.
 *    The router calls `route.options.component.preload()` and waits for it
 *    with the route's `beforeLoad` and `loader`, so the chunk is IN MEMORY
 *    before the screen is first rendered: no Suspense fallback flashes on
 *    navigation, and the previous page stays up while the next one loads.
 *    Without this the route would still work, but through a suspended render.
 *
 *  - `lazyWidget(importer, name)` is for something heavy INSIDE a page — a
 *    live Blockly specimen on a documentation page, a chart, a simulator. It
 *    is not known to the router, so it does show a fallback: a box of the
 *    stated height, so the text around it does not jump when the widget lands.
 *
 * `test/route-splitting.test.ts` fails if a screen that needs Blockly becomes
 * statically reachable from `main.tsx` or from the docs screens, and
 * `scripts/bundle-budget.mjs` asserts the same thing, with byte budgets,
 * against the built output.
 */
import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { lazyRouteComponent } from "@tanstack/react-router";

export { lazyRouteComponent as lazyScreen };

/** Anything `lazyScreen` returns. `preload` is cleared once the chunk is in. */
interface Preloadable {
  preload?: (() => Promise<unknown> | undefined) | undefined;
}

/**
 * Mark a route component as needing `chunks` before it can render.
 *
 * Returns the same function, so `component: screen(function WorkspaceRoute() {
 * … }, Workspace)` reads like the route it replaced.
 */
export function screen<TProps>(
  component: (props: TProps) => ReactNode,
  ...chunks: readonly Preloadable[]
): ((props: TProps) => ReactNode) & { preload: () => Promise<void> | undefined } {
  return Object.assign(component, {
    preload: () => {
      // Read `preload` at call time: once a chunk has loaded there is nothing
      // to wait for, and returning `undefined` keeps a repeat navigation on
      // the router's synchronous path — exactly what a static import gave.
      const pending = chunks.flatMap((chunk) => {
        const loading = chunk.preload?.();
        return loading ? [loading] : [];
      });
      return pending.length === 0 ? undefined : Promise.all(pending).then(() => undefined);
    },
  });
}

export interface LazyWidgetOptions {
  /**
   * Reserved while the widget loads, in CSS pixels — roughly what it will
   * occupy. A fallback of the wrong height is a layout shift the reader sees.
   */
  minHeight: number;
  /** Said to a screen reader while the box is empty. */
  label?: string;
}

/**
 * A component whose code is fetched the first time it is rendered.
 *
 * For anything heavy that sits inside a page rather than being one: the page's
 * own chunk stays small and the widget's cost is paid by the readers who reach
 * it. The import must be a string literal, like every `import()` in this app
 * (`test/route-splitting.test.ts` refuses one it cannot follow).
 */
export function lazyWidget<TModule extends Record<string, unknown>, TName extends keyof TModule>(
  importer: () => Promise<TModule>,
  name: TName,
  options: LazyWidgetOptions,
): TModule[TName] extends ComponentType<infer TProps> ? (props: TProps) => ReactNode : never {
  const Inner = lazy(async () => ({
    default: (await importer())[name] as ComponentType<Record<string, unknown>>,
  }));
  function LazyWidget(props: Record<string, unknown>) {
    return (
      <Suspense
        fallback={
          <div
            role="status"
            aria-busy="true"
            aria-label={options.label ?? "Loading"}
            style={{ minHeight: options.minHeight }}
          />
        }
      >
        <Inner {...props} />
      </Suspense>
    );
  }
  return LazyWidget as never;
}
